import React, { LegacyRef, Ref, useEffect, useLayoutEffect } from "react"
import {AntPopover} from "./react/ant/antPopover"
import {ApplicationState} from "../framework/applicationState"
import {Permission} from "../framework/permission"
import b_ from 'b_'
import {observer} from "mobx-react"
import {CaretDownOutlined, HomeOutlined, SyncOutlined} from '@ant-design/icons'
import './accountSelector.less'
import {makeAutoObservable, runInAction} from "mobx"
import {Section} from 'controls/react/layout/section'
import {Toolbar, ToolbarItemPosition, ToolbarItemsSet} from 'controls/react/layout/toolbar'
import { AntButton } from "./react/ant/antButton"
import {AntInput} from "./react/ant/antInput"
import {ActionButtons} from "./react/form"
import {AntTree, AntTreeRef} from "./react/ant/antTree"
import Cookies from "../core/cookies"
import Utils from "../tools/utils"
import {getAccountsForAccountSelect} from "../areas/administration/accounts/api"
import { apiFetch } from "framework/api"
import type { TreeDataNode } from 'antd'
import {flatTree, getPath} from "../tools/tree"
import Settings from "../settings"
import initCollapseMotion from "antd/es/_util/motion"
import type { CSSMotionProps } from 'rc-motion';

const b = b_.with('account-selector')

const i = require('core/localization/localization').translator()

export interface Account {
	id: string
	items: Account[]
	name: string
}

const getParentKey = (key: React.Key, tree: TreeDataNode[]): React.Key => {
	let parentKey: React.Key
	for (let i = 0; i < tree.length; i++) {
		const node = tree[i]
		if (node.children) {
			if (node.children.some((item) => item.key === key)) {
				parentKey = node.key
			} else if (getParentKey(key, node.children)) {
				parentKey = getParentKey(key, node.children)
			}
		}
	}
	return parentKey!
}

class AccountSelectorStore {
	popupOpened: boolean = false
	accounts: Account[]
	loading: boolean = false
	searchValue: string = ''

	manualExpandedKeys: boolean = true
	savedExpandedKeys: string[]

	selectedAccountId: string
	treeComponent: AntTreeRef
	loaded: boolean = false
	motionEnded: boolean = false
	motionStarted = false
	motion: CSSMotionProps
	scrolled = false

	constructor() {
		makeAutoObservable(this)

		this.motion = this.getMotion()
	}

	getMotion() {
		// code from ant's tree component
		const motion = Object.assign({}, initCollapseMotion('ant'), {
			motionAppear: false
		});
		const originalOnAppearEnd = motion.onAppearEnd
		motion.onAppearEnd = (node, event) => {
			originalOnAppearEnd?.(node, event)
			this.motionEnded = true
		}

		const originalOnAppearStart = motion.onAppearStart
		motion.onAppearStart = (node, event) => {
			originalOnAppearStart?.(node, event)
			this.motionStarted = true
		}

		return motion
	}

	onNameClick = async () => {
		if(!this.hasPermissions) {
			return
		}
		this.popupOpened = !this.popupOpened
		if(this.popupOpened) {
			await this.loadAccounts()
			this.highlightCurrentAccount()
		}
	}

	onMainAccountClick = () => {
		if (Cookies.get('CeesoftAccountId') !== Cookies.get('CeesoftCurrentAccountId')) {
			Cookies.create('CeesoftCurrentAccountId', Cookies.get('CeesoftAccountId'), Settings.COOKIE_TIMEOUT)
			Cookies.create('CeesoftCurrentAccountName', Cookies.get('CeesoftAccountName'), Settings.COOKIE_TIMEOUT)
			Cookies.erase("CeesoftParentAccountId")
			Cookies.erase("CeesoftParentAccountName")
			this.popupOpened = false
			Utils.redirectTo(Settings.httpRoot)
		}
	}

	get isDefaultAccount() {
		return Cookies.get('CeesoftAccountId') === Cookies.get('CeesoftCurrentAccountId')
	}



	loadAccounts = async ({force} : {force: boolean} = {force: false}) => {
		if(this.accounts && !force) {
			return
		}
		this.loaded = false
		this.motionEnded = false
		this.loading = true
		this.scrolled = false
		const result = await apiFetch(getAccountsForAccountSelect(Cookies.get('CeesoftAccountId')))
		if (result.success) {
			runInAction(() => {
				this.accounts = [result.data as Account]
				this.manualExpandedKeys = true
				if(result.data.id === ApplicationState.accountId) { // root account selected
					this.savedExpandedKeys = [result.data.id]
				} else {
					this.savedExpandedKeys = getPath(this.accounts, ApplicationState.accountId)
				}
				this.loading = false
				this.loaded = true
			})

		} else {
			// @ts-ignore
			Utils.showInfo(i('Alert'), result.message, result.details)
		}
		this.loading = false
	}

	mapAccountsToTree = (accounts: Account[]) : TreeDataNode[] => {
		return accounts?.map(x => ({
			key: x.id,
			title: x.name,
			children: this.mapAccountsToTree(x.items)
		}))
	}

	onRefreshClick = async () => {
		await this.loadAccounts({force: true})
	}

	onSave = () => {
		this.popupOpened = false
		const account = flatTree(this.accounts, x => x.items).find(x => x.id === this.selectedAccountId)
		const {id, name} = account
		const path = getPath(this.accounts, this.selectedAccountId)
		const isChild = path.length > 0
		const timeout = Settings.COOKIE_TIMEOUT
		if (isChild) {
			Cookies.create('CeesoftParentAccountId', Cookies.get('CeesoftCurrentAccountId'), timeout)
			Cookies.create('CeesoftParentAccountName', Cookies.get('CeesoftCurrentAccountName'), timeout)
		} else {
			if (id === Cookies.get('CeesoftParentAccountId')) {
				Cookies.create('CeesoftParentAccountId', '', timeout)
				Cookies.create('CeesoftParentAccountName', '', timeout)
			}
		}
		Cookies.create('CeesoftCurrentAccountId', id, timeout)
		Cookies.create('CeesoftCurrentAccountName', name, timeout)
		Cookies.erase('CeesoftAccountLogoId')
		ApplicationState.accountName = name
		ApplicationState.accountId = id
		Utils.redirectTo(Settings.httpRoot)
	}

	onCancel = () => {
		this.searchValue = ''
		this.savedExpandedKeys = [this.accounts[0].id]
		this.popupOpened = false
	}

	get hasPermissions(): boolean {
		return ApplicationState.hasPermissions(Permission.AccountList, Permission.AccountTraverse)
	}

	onExpand = (keys: React.Key[]) => {
		this.manualExpandedKeys = true
		this.savedExpandedKeys = keys as string[]
	}

	get expandedKeys() {
		if(this.manualExpandedKeys) {
			return this.savedExpandedKeys
		}
		const newKeys =  this.flatTreeData
			.map((item) => {
				if ((item.title as string).toLowerCase().indexOf(this.searchValue.toLowerCase()) > -1) {
					return getParentKey(item.key, this.fullTreeData)
				}
				return null
			})
			.filter((item, i, self): item is React.Key => !!(item && self.indexOf(item) === i))
		return newKeys
	}

	setSearchValue = (value: string) => {
		this.manualExpandedKeys = false
		this.searchValue = value
	}


	filter = (data: TreeDataNode[]): TreeDataNode[] => {
		return data?.map((item) => {
			const strTitle = item.title as string
			const index = strTitle.toLowerCase().indexOf(this.searchValue.toLowerCase())
			const beforeStr = strTitle.substring(0, index)
			const searchStr = strTitle.substring(index, this.searchValue.length + index)
			const afterStr = strTitle.slice(index + this.searchValue.length)
			const title =
				index > -1 ? (
					<span>
							{beforeStr}
						<span className={b('search-highlight')}>{searchStr}</span>
						{afterStr}
					</span>
				) : (
					<span>{strTitle}</span>
				)
			const children = this.filter(item.children)
			if (index < 0 && !children?.length) {
				return null
			}
			return { title, key: item.key, children: this.filter(item.children) }
		}).filter(x => x)
	}

	onTreeSelect = (keys: React.Key[]) => {
		this.selectedAccountId = keys[0] as string
	}

	get fullTreeData() : TreeDataNode[] {
		return this.mapAccountsToTree(this.accounts)
	}

	get flatTreeData() : TreeDataNode[] {
		return flatTree(this.fullTreeData, x => x.children)
	}

	get treeData() {
		if(!this.searchValue) {
			return this.fullTreeData
		}
		return this.filter(this.fullTreeData)
	}

	popupOpenedChange = (open: boolean) => {
		this.popupOpened &&= open
	}

	highlightCurrentAccount() {
		this.selectedAccountId = ApplicationState.accountId
	}

	get saveDisabled() {
		return !this.selectedAccountId || this.selectedAccountId == ApplicationState.accountId
	}

	get selectedKeys() {
		return this.selectedAccountId ? [this.selectedAccountId] : []
	}
}

const AccountSelectorPopover = observer(({store}: {store: AccountSelectorStore}) => {
	useEffect(() => {
		requestIdleCallback(() => {
			if(!store.loaded || store.scrolled) return
			if(store.motionStarted && !store.motionEnded) return
			store.treeComponent?.scrollTo({key: ApplicationState.accountId, align: "top"})
			store.scrolled = true
		})
	}, [store.loaded, store.motionEnded, store.scrolled])

	return <Section
		appearance={'none'}
		containerClass={b('popover')}
		contentClass={b('popover-content')}
		childrenPadding={true}
		contentPadding={true}
		overlaySpinner={true}
		contentOverlay={store.loading}
		hasFooter={true}
	>
		<Toolbar>
			<ToolbarItemsSet position={ToolbarItemPosition.BEFORE_TITLE}>
				<AntButton type={'default'} onClick={store.onMainAccountClick} disabled={store.isDefaultAccount}>
					<HomeOutlined/>
				</AntButton>
				<AntInput className={b('filter')} value={store.searchValue} onChange={store.setSearchValue}/>
				<AntButton type={'default'} onClick={store.onRefreshClick}>
					<SyncOutlined />
				</AntButton>
			</ToolbarItemsSet>
		</Toolbar>
		<AntTree
			componentRef={(ref) => store.treeComponent = ref}
			onSelect={store.onTreeSelect}
			treeData={store.treeData}
			onExpand={store.onExpand}
			expandedKeys={store.expandedKeys}
			autoExpandParent={!store.manualExpandedKeys}
			selectedKeys={store.selectedKeys}
			height={700}
			motion={store.motion}
		/>
		<Toolbar appearance={'transparent'} containerClass={b('actions-toolbar')}>
			<ActionButtons actionButtonLabel={i('Select')} onSave={store.onSave} onCancel={store.onCancel} saveDisabled={store.saveDisabled} className={b('actions')}/>
		</Toolbar>
	</Section>
})

export const AccountSelector = observer(() => {
	// click permission: ACCOUNT_LIST && ACCOUNT_TRAVERSE
	const [store] = React.useState(() => new AccountSelectorStore())

	return <AntPopover
		content={<AccountSelectorPopover store={store} />}
		arrow={false}
		trigger={'click'}
		open={store.popupOpened}
		onOpenChange={store.popupOpenedChange}
	>
		<div className={b()} onClick={store.onNameClick}>
			<span className={b('name')}>{ApplicationState.accountName}</span>
			{store.hasPermissions && <CaretDownOutlined />}
		</div>
	</AntPopover>
})
